/**
 * Tile space and world space.
 *
 * World space is continuous: x and z are ground plane coordinates in tiles, y is
 * visual height and never affects gameplay. Tile space is the floor of world space,
 * and is the authoritative game position, sampled on the tick boundary.
 */

import { POSITION_PRECISION, ZONE_SHIFT, ZONE_SIZE } from './constants';

/** A continuous position on one plane. */
export interface WorldPos {
  x: number;
  z: number;
  plane: number;
}

/** A discrete tile on one plane. This is what game logic reads. */
export interface TilePos {
  tx: number;
  tz: number;
  plane: number;
}

export function tileOf(pos: WorldPos): TilePos {
  return { tx: Math.floor(pos.x), tz: Math.floor(pos.z), plane: pos.plane };
}

/** Center of a tile in world space. Entities path between tile centers. */
export function centerOf(tile: TilePos): WorldPos {
  return { x: tile.tx + 0.5, z: tile.tz + 0.5, plane: tile.plane };
}

export function sameTile(a: TilePos, b: TilePos): boolean {
  return a.tx === b.tx && a.tz === b.tz && a.plane === b.plane;
}

/**
 * Chebyshev distance, which is how every range check in the game is measured.
 * Diagonally adjacent counts as distance 1, exactly as it does in OSRS.
 */
export function tileDistance(a: TilePos, b: TilePos): number {
  return Math.max(Math.abs(a.tx - b.tx), Math.abs(a.tz - b.tz));
}

/** True Euclidean distance in world space. For visuals and audio falloff only. */
export function worldDistance(a: WorldPos, b: WorldPos): number {
  const dx = a.x - b.x;
  const dz = a.z - b.z;
  return Math.sqrt(dx * dx + dz * dz);
}

export function isAdjacent(a: TilePos, b: TilePos): boolean {
  return a.plane === b.plane && tileDistance(a, b) === 1;
}

/** Packs a tile into a single integer for use as a map key. */
export function tileKey(tx: number, tz: number, plane: number): number {
  return ((plane & 0x3) << 28) | ((tx & 0x3fff) << 14) | (tz & 0x3fff);
}

export function zoneOf(tx: number, tz: number): { zx: number; zz: number } {
  return { zx: tx >> ZONE_SHIFT, zz: tz >> ZONE_SHIFT };
}

export function zoneKey(zx: number, zz: number, plane: number): number {
  return ((plane & 0x3) << 24) | ((zx & 0xfff) << 12) | (zz & 0xfff);
}

/** The zone key for a tile, which is the common case of zoneOf then zoneKey. */
export function zoneKeyOf(tx: number, tz: number, plane: number): number {
  const { zx, zz } = zoneOf(tx, tz);
  return zoneKey(zx, zz, plane);
}

/** Origin tile of a zone, used as the base for quantized relative positions. */
export function zoneOrigin(zx: number, zz: number): { tx: number; tz: number } {
  return { tx: zx * ZONE_SIZE, tz: zz * ZONE_SIZE };
}

/** Quantizes a world coordinate to 1/64 of a tile for transmission. */
export function quantize(value: number): number {
  return Math.round(value * POSITION_PRECISION);
}

export function dequantize(value: number): number {
  return value / POSITION_PRECISION;
}

/** The eight compass directions, in the order used on the wire. */
export const DIRECTIONS = [
  { dx: 0, dz: 1 }, // north
  { dx: 1, dz: 1 },
  { dx: 1, dz: 0 }, // east
  { dx: 1, dz: -1 },
  { dx: 0, dz: -1 }, // south
  { dx: -1, dz: -1 },
  { dx: -1, dz: 0 }, // west
  { dx: -1, dz: 1 },
] as const;

/** Facing angle in radians, derived from a movement vector. Purely visual. */
export function headingOf(dx: number, dz: number): number {
  return Math.atan2(dx, dz);
}
